import styled, { css } from 'styled-components';
import { FontSizes } from '../../../common/typography/typography.styles';
import { BasePaddingComponent } from '../../../common/base-padding/base-padding.component';
import { Container } from '../common/container.styles';
import { FlexContainer } from '../../../common/flex-container/flex-container.styles';
import { Section } from '../common/section.styles';
import {
  PrimaryButton,
  SecondaryButton,
} from '../../../common/button/button.styles';
import {
  InfoTextContainer,
  InputContainer,
} from '../../../common/form-field-text/form-field-text.styles';
import { SpinnerCircle } from '../../../common/spinner/spinner.styles';
import { InputField, LabelWrapper } from '../../../common/input/input.styles';
import { SectionSubtitle } from '../common/typography.styles';
import { InputMessageContainer } from '../../../common/input-message/input-message.styles';
import { BREAKPOINTS } from '../../../constants/breakpoints';
import { InputSearchContainer } from '../../../common/input-search/input-search.styles';

interface IDataInputFieldProps {
  $minWidth?: string;
  $isFieldValid?: boolean;
  $isDisabled?: boolean;
}
interface IDataInputLabelButtonProps {
  $isInvalid?: boolean;
  $errorColor?: string;
}

export const DesktopWidthLimitCalculator = '1100px';

export const WidgetCalculatorWrapper = styled(Container)`
  @media (max-width: ${DesktopWidthLimitCalculator}) {
    width: 100%;
    padding: 0;
  }
`;

export const WidgetCalculatorFrameWrapper = styled(Container)`
  --padding: 0;

  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;
`;

export const WidgetCalculatorSection = styled(Section)`
  @media (max-width: ${DesktopWidthLimitCalculator}) {
    padding-top: 0;
  }
`;

export const BackgroundImage = styled.div`
  z-index: -1;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  max-height: 49.2rem;
  border-radius: 30px;

  object-fit: cover;
  object-position: center;
  border-radius: var(--container-border-radius);

  @media (max-width: ${DesktopWidthLimitCalculator}) {
    height: 40%;
  }
`;

export const WidgetCalculatorContainer = styled.div`
  --container-border-radius: 3rem;
  position: relative;
  isolation: isolate;
  width: 100%;
  height: 100%;
  min-height: 49.2rem;
  padding: 4rem 1.6rem 4.8rem;

  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;

  background: linear-gradient(
      102deg,
      rgba(0, 112, 204, 0.8) 38.29%,
      rgba(0, 56, 102, 0.8) 100%
    ),
    #0070cc;

  @media (min-width: calc(${DesktopWidthLimitCalculator} + 1px)) {
    padding: 8rem 6rem;
    border-radius: var(--container-border-radius);
  }

  ${SectionSubtitle} {
    padding: 0.6rem 1.6rem;
    border-radius: 0.8rem;
    margin-bottom: 0.8rem;
    line-height: 2.4rem;
    font-size: ${FontSizes.mediumPlus};
    background-color: ${({ theme }) =>
      theme.colors.namedColors.secondaryPrimary};

    @media (max-width: ${DesktopWidthLimitCalculator}) {
      font-size: ${FontSizes.normal};
    }
  }
`;

export const CalculatorSubtitle = styled.h2`
  color: ${({ theme }) => theme.colors.namedColors.white};
  font-family: ${({ theme }) => theme.fonts.body.fontFamily};
  font-size: ${FontSizes.mediumPlus};
  font-weight: 500;
  letter-spacing: 0.5px;
  margin-bottom: 4rem;
`;

export const Title = styled.h1`
  color: ${({ theme }) => theme.colors.namedColors.white};
  font-family: ${({ theme }) => theme.fonts.body.fontFamily};
  font-size: 6rem;
  font-style: normal;
  font-weight: 700;
  line-height: 7rem;
  letter-spacing: 1px;
  margin-bottom: 0.8rem;

  @media (max-width: ${DesktopWidthLimitCalculator}) {
    font-size: ${FontSizes.huge};
    line-height: 4.8rem;
    margin-bottom: 1rem;
  }
`;

export const ParcelTabsContainer = styled.div`
  display: flex;
  gap: 4px;

  @media (max-width: ${BREAKPOINTS.mobile.css}) {
    margin-bottom: 1.2rem;
    flex-wrap: wrap;
  }
`;

interface ParcelTabProps {
  $isActive?: boolean;
}

export const ParcelTab = styled.div<ParcelTabProps>`
  cursor: pointer;
  display: flex;
  padding: 1.2rem 1.6rem;
  justify-content: center;
  align-items: center;
  gap: 0.8rem;
  border-radius: 1.2rem 1.2rem 0 0;

  color: ${({ $isActive, theme }) =>
    $isActive
      ? theme.colors.parcelTab.colorActive
      : theme.colors.namedColors.white};
  background: ${({ $isActive, theme }) =>
    $isActive
      ? theme.colors.namedColors.white
      : theme.colors.baseColors.blue200};

  ${({ $isActive, theme }) => {
    if (!$isActive)
      return css`
        border: 1px solid ${theme.colors.baseColors.blue175};
        border-bottom: none;
      `;
  }}

  @media (min-width: calc(${DesktopWidthLimitCalculator} + 1px)) {
    padding: 1.8rem 2.4rem;
  }

  @media (max-width: ${BREAKPOINTS.mobile.css}) {
    border-radius: 1.2rem;
    min-width: fit-content;

    ${({ $isActive, theme }) => {
      if (!$isActive)
        return css`
          border-bottom: 1px solid ${theme.colors.baseColors.blue175};
        `;
    }}
  }
`;

interface ParcelTabContentProps {
  $fontWeight?: string;
  $lineHeight?: string;
}

export const ParcelTabContent = styled.div<ParcelTabContentProps>`
  text-align: center;
  font-family: ${({ theme }) => theme.fonts.body.fontFamily};
  font-size: ${FontSizes.normal};
  line-height: ${({ $lineHeight = '1.6rem' }) => $lineHeight};
  font-style: normal;
  font-weight: ${({ $fontWeight = '700' }) => $fontWeight};
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;

  & > svg {
    width: 2rem;
    height: 2rem;
  }

  & > img {
    width: 2rem;
  }

  @media (min-width: calc(${DesktopWidthLimitCalculator} + 1px)) {
    font-size: ${FontSizes.medium};
    line-height: 2.2rem;
  }
`;

interface CalculatoContainerProps {
  $hidden?: boolean;
}

export const CalculatoContainer = styled.div<CalculatoContainerProps>`
  display: ${({ $hidden }) => ($hidden ? 'none' : 'flex')};
  width: 100%;
  height: 100%;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;

  border-radius: 1.6rem;
  border-top-left-radius: 0;
  background: ${({ theme }) => theme.colors.namedColors.white};

  @media (max-width: ${BREAKPOINTS.mobile.css}) {
    border-radius: 1.6rem;
  }

  label[data-disabled='true'] {
    background-color: transparent;
  }
`;

export const DataInputFields = styled.div`
  width: 100%;
  height: 100%;
  min-height: 104px;
  padding: 1.2rem;

  display: flex;
  gap: 2.4rem;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: nowrap;

  border-radius: 0 1.6rem 1.6rem 1.6rem;
  background: ${({ theme }) => theme.colors.namedColors.white};

  @media screen and (min-width: calc(${DesktopWidthLimitCalculator} + 1px)) {
    gap: 3.2rem;
  }

  @media (max-width: ${DesktopWidthLimitCalculator}) {
    flex-direction: column;
  }

  & > *:nth-child(1) {
    width: 80%;
    @media (max-width: ${DesktopWidthLimitCalculator}) {
      width: 100%;
    }
  }

  @media (max-width: ${BREAKPOINTS.mobile.css}) {
    border-radius: 1.6rem;
  }
`;

export const DataInputFieldsContainer = styled(FlexContainer)`
  justify-content: start;
  gap: 1.2rem;
  @media (max-width: ${DesktopWidthLimitCalculator}) {
    flex-direction: column;
  }
`;

export const DataInputField = styled.div<IDataInputFieldProps>`
  display: flex;
  position: relative;
  width: 100%;
  height: 8rem;
  padding: 0.4rem 0.8rem 0 1.2rem;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  flex: 0 1 24.8rem;
  gap: 0.4rem;

  &:nth-child(n + 4) {
    @media (max-width: ${DesktopWidthLimitCalculator}) {
      width: 50%;
    }
  }

  ${InputField} {
    padding: 0.8rem 0;
    font-family: ${({ theme }) => theme.fonts.body.fontFamily};
    font-size: ${FontSizes.mediumPlus};
    font-style: normal;
    font-weight: 500;
    line-height: 2rem;
    letter-spacing: 0.5px;
    min-width: 80px;

    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    border: none;

    @media (max-width: ${DesktopWidthLimitCalculator}) {
      font-size: 1.6rem;
      line-height: 2.2rem;
    }
  }

  input {
    color: ${({ $isFieldValid = true, theme }) =>
      $isFieldValid
        ? theme.colors.baseColors.grey150
        : theme.colors.baseColors.red100};
  }

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      ${InputField}, input {
        background: none;
        // required on iOS
        opacity: 1;
      }
    `}

  & ${InfoTextContainer} {
    span {
      color: ${({ theme }) => theme.colors.baseColors.grey150};
    }
  }

  @media (max-width: ${DesktopWidthLimitCalculator}) {
    height: 6.4rem;
    flex: 1 1 6.4rem;
    border: 1px solid #d1d1d1;
    border-radius: 1rem;
    padding: 0.8rem 1.2rem 0.4rem 1.6rem;
    gap: 0;
  }
`;

export const MeasureInputField = styled(DataInputField)`
  flex: 1 1 100px;
  width: 100px;
  flex-shrink: 0;
`;

export const DataInputLabelButton = styled.button.attrs({
  type: 'button',
})<IDataInputLabelButtonProps>`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 0.4rem;
  border: none;
  background: transparent;

  color: ${({
    $isInvalid = false,
    theme,
    $errorColor = theme.colors.baseColors.red100,
  }) => ($isInvalid ? $errorColor : theme.colors.baseColors.grey100)};
  font-family: ${({ theme }) => theme.fonts.body.fontFamily};
  font-size: ${FontSizes.normal};
  font-style: normal;
  font-weight: 500;
  line-height: 2rem;
  letter-spacing: 0.5px;
  padding: 0;
  cursor: pointer;
  outline: none;
`;

export const DimensionFlexContainer = styled(FlexContainer)`
  width: 100%;
  flex-direction: row;
  border: 1px solid #d1d1d1;
  border-radius: 1rem;

  & > * {
    border: none;
    margin: 0.8rem 0;
    height: 4.8rem;
  }

  & > *:nth-child(1) {
    border-radius: 0;
    @media (max-width: calc(${DesktopWidthLimitCalculator} + 1px)) {
      border-right: 1px solid #d1d1d1;
    }
  }

  @media (min-width: calc(${DesktopWidthLimitCalculator} + 1px)) {
    border: none;
    flex: 1 1 24.8rem;
    gap: 1.2rem;
  }
`;

export const DividerPipe = styled.div`
  width: 0.1rem;
  height: 6rem;
  background-color: ${({ theme }) => theme.colors.baseColors.grey50};

  @media (max-width: calc(${DesktopWidthLimitCalculator} + 1px)) {
    display: none;
  }
`;

export const CalculateButtonContainer = styled.div`
  width: 100%;
  min-width: 210px;
  align-self: center;
  white-space: nowrap;
  ${PrimaryButton},
  ${SecondaryButton} {
    max-width: 100%;
    height: 6.4rem;
    transition: opacity 0.2s;
    border-radius: 1.2rem;
    ${SpinnerCircle} {
      border-width: 3px;
    }

    &:hover {
      opacity: 0.8;
    }

    svg {
      width: 2.1rem;
      height: 2.1rem;
    }
  }

  @media (min-width: calc(${DesktopWidthLimitCalculator} + 1px)) {
    max-width: 20rem;
    ${PrimaryButton}, ${SecondaryButton} {
      height: 8rem;
      font-size: ${FontSizes.mediumPlus};
    }
  }
`;

export const MenuItemsContainer = styled.div`
  width: calc(100% + 25px);
  margin-left: -2.4rem;
  position: absolute;
  top: 100%;
  z-index: 1000;
  padding-top: 12px;
  overflow: hidden;

  &:before {
    display: block;
    content: '';
    width: 100%;
    height: 1px;
    background-color: #dcdde0;
    position: absolute;
    top: 12px;
    left: 0;
  }

  &:after {
    display: block;
    content: '';
    width: calc(100% + 2px);
    height: 12px;
    background-color: #fff;
    position: absolute;
    top: 0;
    left: -1px;
    z-index: 10;
  }

  @media (max-width: ${DesktopWidthLimitCalculator}) {
    width: calc(100% + 2px);
    margin-left: -1.7rem;
    &:before,
    &:after {
      display: none;
    }
  }
`;

export const MenuItemsContainerInner = styled.div`
  display: block;
  width: 100%;
  max-height: 21.6rem;
  background: ${({ theme }) => theme.colors.namedColors.white};
  border-radius: 0 0 1rem 1rem;
  border: 1px solid #dcdde0;
  border-top: 0;
  box-shadow: 0px 2px 3px 0px #05236b14;
  overflow-y: auto;
  overflow-x: hidden;

  @media (max-width: ${DesktopWidthLimitCalculator}) {
    width: 100%;
    border: 1px solid #d1d1d1;
    border-radius: 1rem;
    top: 7rem;
    margin-left: 0;
    padding-top: 0;
    &:before,
    &:after {
      display: none;
    }
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    border-radius: 6px;
    background: ${({ theme }) => theme.colors.baseColors.grey65};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.baseColors.grey100};
    border-radius: 6px;
  }

  .nearMeButton {
    svg {
      path {
        fill: ${({ theme }) => theme.colors.baseColors.grey100};
      }
    }
    color: ${({ theme }) => theme.colors.baseColors.grey150};
    font-size: ${FontSizes.medium};
    font-style: normal;
    font-weight: 400;
    line-height: 2.2rem; /* 137.5% */
    letter-spacing: 0.5px;
    margin: 1rem 1.6rem;
  }
`;

export const CalcInputWrapper = styled.div`
  width: 100%;
  .calc-input-skeleton {
    height: 2rem !important;
    margin: 0.8rem 0;
    width: 11rem !important;
    border-radius: 6px !important;

    @media (max-width: calc(${DesktopWidthLimitCalculator})) {
      height: 2.2rem !important;
      width: 15rem !important;
    }
  }
`;

export const MenuItemsSkeletonContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem 0;

  .calc-menu-item-skeleton {
    margin: 0 1.6rem;
    margin-bottom: 1rem;
    width: calc(100% - 3.2rem);

    &:last-of-type {
      margin-bottom: 0;
    }
  }
`;

export const PaddingContainer = styled(BasePaddingComponent('div'))`
  width: 100%;
`;

export const VolumetricWeightWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #ffbe0a;
  font-size: ${FontSizes.normal};

  svg {
    width: 1.28em;
    height: 1.28em;
  }

  @media (max-width: 650px) {
    align-items: start;

    svg {
      width: 3em;
    }
  }
`;

export const MessagesContainer = styled(FlexContainer)`
  padding: 0 12px 12px 12px;
  &:empty {
    display: none;
  }

  @media (max-width: calc(${DesktopWidthLimitCalculator})) {
    padding: 12px 0 0 0;
  }
`;

export const InputPlusErrorContainer = styled.div`
  width: 100%;
`;

export const TrackingFormContainer = styled.div<{ $isSearchVisible?: boolean }>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: start;
  gap: 3.2rem;
  padding: 1.2rem;

  ${({ $isSearchVisible }) =>
    $isSearchVisible &&
    `
    ${LabelWrapper} {
      padding-top: 1.2rem;
    }
    ${InputMessageContainer} {
      padding-bottom: 1.2rem;
  }`}

  ${InputContainer} {
    width: 33rem;
  }

  ${InputSearchContainer} {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 8rem;
    @media (max-width: 650px) {
      min-height: 6.4rem;
    }
    input {
      width: 30rem;
      padding: 0.8rem 0 0.8rem 1.2rem;
      font-size: ${FontSizes.mediumPlus};
      line-height: 2rem;
      font-weight: 500;
      border-radius: 0;
      color: ${({ theme }) => theme.colors.namedColors.grey150};
      margin-top: 4px;
      &::placeholder {
        font-size: ${FontSizes.normal};
        color: ${({ theme }) => theme.colors.namedColors.grey80};
      }
      &::-webkit-search-cancel-button {
        -webkit-appearance: none;
        appearance: none;
      }
    }
  }
  ${CalculateButtonContainer} {
    width: 20rem;
  }

  ${PrimaryButton} {
    &:hover {
      opacity: 1;
    }
  }

  ${LabelWrapper} {
    margin-bottom: 0;
    padding-left: 1.2rem;
    label {
      cursor: pointer;
    }
  }
  ${CalculateButtonContainer} {
    align-self: flex-start;
  }
  ${InputMessageContainer} {
    margin-top: 2rem;
    padding-left: 0;
  }

  @media (max-width: 650px) {
    flex-direction: column;
    gap: 1.6rem;
    ${InputSearchContainer} {
      width: 100%;
    }
    ${InputContainer} {
      width: 100%;
      input {
        max-height: 6.4rem;
        height: 6.4rem;
      }
    }
    ${InputMessageContainer} {
      margin-top: 1.2rem;
    }

    ${CalculateButtonContainer} {
      width: 100%;
    }

    ${LabelWrapper} {
      padding-left: 0;
    }

    ${InputSearchContainer} {
      display: flex;
      flex-direction: column;
      justify-content: center;
      width: 100%;
      position: relative;
      height: 6.4rem;
      border: 1px solid #d1d1d1;
      border-radius: 1rem;
      padding: 0.8rem 1.2rem 0.8rem 1.6rem;
    }

    ${InputContainer} {
      input {
        padding: 0;
        border: 0;
        height: auto;
        background: transparent;
      }
    }
  }
`;
